import { ActivityFieldKey } from '@api';

/**
 * KT-903 closed catalogue of layout fields (wire keys from OpenAPI `ActivityFieldKey`).
 *
 * This is **not** `ActivityDetailEditableField` from `activity-inline-edit.constants`:
 * autosave commits also use `visibility`, `subtype`, `subtopic`, and `topic`, which are outside
 * the layout catalogue.
 *
 * The gear panel and the edit form keep separate label keys
 * ({@link ACTIVITY_FIELD_CARD_MENU_LABEL_KEY} vs {@link ACTIVITY_FIELD_FORM_LABEL_KEY}).
 * The type field uses the same word in both places (KT-1185).
 */

/** Transloco keys relative to the `ACTIVITY_BROWSER` scope. */
export const ACTIVITY_FIELD_CARD_DISPLAY_MENU_I18N_PREFIX = 'DETAIL.EDIT_PAGE.CARD_DISPLAY_MENU';

const CARD_MENU_ROWS_PREFIX = `${ACTIVITY_FIELD_CARD_DISPLAY_MENU_I18N_PREFIX}.ROWS`;

/**
 * Default catalogue order (1–12), aligned with backend `ActivityFieldKey` values.
 */
export const ACTIVITY_FIELD_LAYOUT_CATALOGUE_ORDER: readonly ActivityFieldKey[] = [
  ActivityFieldKey.NAME,
  ActivityFieldKey.TYPE,
  ActivityFieldKey.WORKFORM,
  ActivityFieldKey.DURATION,
  ActivityFieldKey.GRADE,
  ActivityFieldKey.SUBJECT,
  ActivityFieldKey.LOCATIONS,
  ActivityFieldKey.TOOLS,
  ActivityFieldKey.DESCRIPTION,
  ActivityFieldKey.INSTRUCTIONS,
  ActivityFieldKey.SECTIONS,
  ActivityFieldKey.CONTEXT_DOCUMENTS,
];

/**
 * Layout fields currently implemented on the edit card and listed in the gear panel (AC-B10).
 * Trim this list when a catalogue field is not yet shipped; the panel filters server layout to this set.
 */
export const SHIPPED_ACTIVITY_FIELD_KEYS: readonly ActivityFieldKey[] = ACTIVITY_FIELD_LAYOUT_CATALOGUE_ORDER;

/** Gear menu row labels (library-card catalogue copy). */
export const ACTIVITY_FIELD_CARD_MENU_LABEL_KEY = {
  [ActivityFieldKey.NAME]: `${CARD_MENU_ROWS_PREFIX}.NAME`,
  [ActivityFieldKey.TYPE]: `${CARD_MENU_ROWS_PREFIX}.ACTIVITY_TYPE`,
  [ActivityFieldKey.WORKFORM]: `${CARD_MENU_ROWS_PREFIX}.WORKFORM`,
  [ActivityFieldKey.DURATION]: `${CARD_MENU_ROWS_PREFIX}.DURATION`,
  [ActivityFieldKey.GRADE]: `${CARD_MENU_ROWS_PREFIX}.GRADE`,
  [ActivityFieldKey.SUBJECT]: `${CARD_MENU_ROWS_PREFIX}.SUBJECT`,
  [ActivityFieldKey.TOOLS]: `${CARD_MENU_ROWS_PREFIX}.TOOLS`,
  [ActivityFieldKey.DESCRIPTION]: `${CARD_MENU_ROWS_PREFIX}.DESCRIPTION`,
  [ActivityFieldKey.INSTRUCTIONS]: `${CARD_MENU_ROWS_PREFIX}.INSTRUCTIONS`,
  [ActivityFieldKey.SECTIONS]: `${CARD_MENU_ROWS_PREFIX}.SECTIONS`,
  [ActivityFieldKey.CONTEXT_DOCUMENTS]: `${CARD_MENU_ROWS_PREFIX}.CONTEXT_DOCUMENTS`,
  [ActivityFieldKey.LOCATIONS]: `${CARD_MENU_ROWS_PREFIX}.LOCATION`,
} satisfies Record<ActivityFieldKey, string>;

/**
 * Edit-card form labels (inline fields and section headings). For FE-4 dynamic layout render.
 */
export const ACTIVITY_FIELD_FORM_LABEL_KEY = {
  [ActivityFieldKey.NAME]: 'DETAIL.EDIT_PAGE.NAME_LABEL',
  [ActivityFieldKey.TYPE]: 'DETAIL.EDIT_PAGE.TYPE_LABEL',
  [ActivityFieldKey.WORKFORM]: 'DETAIL.EDIT_PAGE.WORKFORM_LABEL',
  [ActivityFieldKey.DURATION]: 'DETAIL.EDIT_PAGE.DURATION_LABEL',
  [ActivityFieldKey.GRADE]: 'DETAIL.EDIT_PAGE.GRADE_LABEL',
  [ActivityFieldKey.SUBJECT]: 'DETAIL.EDIT_PAGE.SUBJECT_LABEL',
  [ActivityFieldKey.TOOLS]: 'DETAIL.EDIT_PAGE.TOOLS_LABEL',
  [ActivityFieldKey.DESCRIPTION]: 'DETAIL.EDIT_PAGE.DESCRIPTION_LABEL',
  [ActivityFieldKey.INSTRUCTIONS]: 'DETAIL.INSTRUCTION',
  [ActivityFieldKey.SECTIONS]: `${CARD_MENU_ROWS_PREFIX}.SECTIONS`,
  [ActivityFieldKey.CONTEXT_DOCUMENTS]: 'DETAIL.CONTEXT.LABEL',
  [ActivityFieldKey.LOCATIONS]: 'DETAIL.EDIT_PAGE.LOCATION_LABEL',
} satisfies Record<ActivityFieldKey, string>;

/**
 * Primary aria labels for layout fields on the edit page (matches current `activity-edit-page` bindings).
 */
export const ACTIVITY_FIELD_ARIA_LABEL_KEY = {
  [ActivityFieldKey.NAME]: 'DETAIL.TITLE',
  [ActivityFieldKey.TYPE]: 'DETAIL.TYPE',
  [ActivityFieldKey.WORKFORM]: 'DETAIL.WORKFORM',
  [ActivityFieldKey.DURATION]: 'DETAIL.DURATION_VALUE',
  [ActivityFieldKey.GRADE]: 'DETAIL.GRADE_FIELD',
  [ActivityFieldKey.SUBJECT]: 'DETAIL.SUBJECT',
  [ActivityFieldKey.TOOLS]: 'DETAIL.TOOLS',
  [ActivityFieldKey.DESCRIPTION]: 'DETAIL.DESCRIPTION',
  [ActivityFieldKey.INSTRUCTIONS]: 'DETAIL.INSTRUCTION',
  [ActivityFieldKey.SECTIONS]: `${CARD_MENU_ROWS_PREFIX}.SECTIONS`,
  [ActivityFieldKey.CONTEXT_DOCUMENTS]: 'DETAIL.CONTEXT.LABEL',
  [ActivityFieldKey.LOCATIONS]: 'DETAIL.LOCATION',
} satisfies Record<ActivityFieldKey, string>;
